import React, { useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { CheckCircle } from 'lucide-react';

const PricingSection = styled.section`
  padding: 5rem 0;
  background: white;

  @media (min-width: 768px) {
    padding: 8rem 0;
  }
`;

const PricingContainer = styled.div`
  max-width: 1024px;
  margin: 0 auto;
  padding: 0 1rem;
  text-align: center;

  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }

  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const PricingTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 800;
  margin-bottom: 3rem;
  letter-spacing: -0.025em;

  @media (min-width: 640px) {
    font-size: 2.25rem;
  }

  @media (min-width: 768px) {
    font-size: 3rem;
    margin-bottom: 4rem;
  }
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 3rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PricingCard = styled.div`
  padding: 2rem;
  border-radius: 1rem;
  transition: all 0.3s;
  opacity: 0;
  transform: translateY(20px);

  ${(props) =>
    props.featured
      ? `
    background: linear-gradient(135deg, black, #374151);
    color: white;
    transform: scale(1.05);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);

    @media (min-width: 768px) {
      transform: scale(1.05) translateY(0);
    }
  `
      : `
    background: #f9fafb;
    border: 1px solid #f3f4f6;
  `}

  @media (min-width: 768px) {
    padding: 3rem;
  }
`;

const PlanTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.5rem;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const PriceContainer = styled.div`
  font-size: 2.25rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  letter-spacing: -0.025em;

  @media (min-width: 768px) {
    font-size: 3.75rem;
    margin-bottom: 2rem;
  }
`;

const PricePeriod = styled.span`
  font-size: 1.125rem;
  font-weight: 500;
  opacity: 0.6;

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const FeaturesList = styled.ul`
  margin-bottom: 2rem;
  list-style: none;
  padding: 0;

  li {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    justify-content: center;
    margin-bottom: 0.75rem;
    font-size: 0.875rem;

    @media (min-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const CTAButton = styled.a`
  width: 100%;
  padding: 0.75rem 0;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  display: inline-flex;
  justify-content: center;
  transition: all 0.2s;

  ${(props) =>
    props.featured
      ? `
    background: white;
    color: black;

    &:hover {
      background: #f3f4f6;
    }
  `
      : `
    background: #7c3aed;
    color: white;

    &:hover {
      background: #6d28d9;
    }
  `}

  &:hover {
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  @media (min-width: 768px) {
    padding: 1rem 0;
    font-size: 1.125rem;
  }
`;

const PricingNote = styled.p`
  color: #6b7280;
  font-size: 0.875rem;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const pricing = [
  {
    title: 'Free',
    price: '$0',
    features: ['50 static codes per month', 'Basic templates', 'PNG downloads'],
    cta: 'Get Started',
    featured: false,
  },
  {
    title: 'Pro',
    price: '$5',
    period: '/month',
    features: [
      'Unlimited QR codes',
      'Dynamic QR codes',
      'Advanced analytics',
      'Custom branding',
      'Priority support',
    ],
    cta: 'Start Free Trial',
    featured: true,
  },
];

export default function Pricing() {
  const pricingRef = useRef([]);

  useEffect(() => {
    const fadeObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform =
              entry.target.dataset.featured === 'true'
                ? 'scale(1.05)'
                : 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 },
    );

    pricingRef.current.forEach((ref) => {
      if (ref) {
        ref.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        fadeObserver.observe(ref);
      }
    });

    return () => {
      fadeObserver.disconnect();
    };
  }, []);

  return (
    <PricingSection id="pricing">
      <PricingContainer>
        <PricingTitle>Simple Pricing</PricingTitle>
        <PricingGrid>
          {pricing.map((plan, index) => (
            <PricingCard
              key={index}
              ref={(el) => (pricingRef.current[index] = el)}
              featured={plan.featured}
              data-featured={plan.featured}
            >
              <PlanTitle>{plan.title}</PlanTitle>
              <PriceContainer>
                {plan.price}
                {plan.period && <PricePeriod>{plan.period}</PricePeriod>}
              </PriceContainer>
              <FeaturesList>
                {plan.features.map((feature, idx) => (
                  <li key={idx}>
                    <CheckCircle
                      size={20}
                      color={plan.featured ? '#c084fc' : '#7c3aed'}
                    />
                    <span>{feature}</span>
                  </li>
                ))}
              </FeaturesList>
              <CTAButton href="/qr" featured={plan.featured}>
                {plan.cta}
              </CTAButton>
            </PricingCard>
          ))}
        </PricingGrid>
        <PricingNote>
          Annual pricing available. Save 17% with yearly billing.
        </PricingNote>
      </PricingContainer>
    </PricingSection>
  );
}
